import { supabase } from "@/integrations/supabase/client";
import { addDays, diffDays, inclusiveDayCount } from "@/components/utils/dateUtils";
import type { Database } from "@/integrations/supabase/types";
import type { TranslationKey } from "@/types/i18n.types";

export interface SendQuoteParams {
  draftId: string;
  pdfBase64?: string;
  pdfFilename?: string;
  to: string;
  subject: string;
  shouldSendEmail: boolean;
  sharingMethod: 'create_user' | 'existing_user' | 'shared_link';
  /** Lingua della mail: scegle il template Mailgun '<template>_<lingua>'. */
  language?: 'it' | 'en';
  linkPassword?: string;
  sharedLinkUrl?: string | null;
  variables: {
    client_first_name: string;
    client_last_name: string;
    itinerary_title: string;
    start_date: string;
    end_date: string;
    custom_message?: string;
    shared_link_url?: string | null;
    payment: string;
    logo_url?: string;
    cancellation_policy_notes?: string;
  };
}

export interface SendQuoteResponse {
  success: boolean;
  error?: string;
  errorCode?: string;
  message?: string;
}

/**
 * Centralizes the send-quote edge function invocation.
 * This should be the ONLY place where send-quote is called from the frontend.
 */
export const sendQuote = async (params: SendQuoteParams): Promise<SendQuoteResponse> => {
  try {
    const { data, error } = await supabase.functions.invoke("send-quote", {
      body: JSON.stringify({
        draftId: params.draftId,
        pdfBase64: params.pdfBase64,
        pdfFilename: params.pdfFilename,
        to: params.to,
        subject: params.subject,
        // Backend logic parameters
        shouldSendEmail: params.shouldSendEmail,
        sharingMethod: params.sharingMethod,
        language: params.language,
        linkPassword: params.linkPassword,
        sharedLinkUrl: params.sharedLinkUrl,
        // Mailgun template variables only
        variables: params.variables,
      }),
    });

    if (error) {
      const responseBody = (data as any) || {};
      return {
        success: false,
        error: responseBody?.error || 'send_quote_failed',
        errorCode: responseBody?.error,
        message: responseBody?.message,
      };
    }

    return { success: true };
  } catch (error) {
    console.error('Error sending quote:', error);
    return {
      success: false,
      error: 'send_quote_exception',
      message: error instanceof Error ? error.message : 'Unknown error',
    };
  }
};

/**
 * Ritira un preventivo gia' inviato riportandolo in bozza, cosi' il viaggiatore non lo vede
 * piu' mentre il TA lo corregge e lo rimanda.
 *
 * Non e' un semplice cambio di stato: la RPC rifiuta se il viaggiatore ha gia' pagato o ha
 * un pagamento in corso. Per questo non passa da updateItineraryStatus, che scrive
 * direttamente sulla tabella e non saprebbe fare quel controllo senza una race.
 */
export const revokeItineraryQuote = async (itineraryId: string): Promise<void> => {
  const { error } = await supabase.rpc('revoke_itinerary_quote' as any, {
    p_itinerary_id: itineraryId,
  });

  if (error) throw error;
};

/** Errori della revoca, tradotti in chiavi i18n. Il messaggio del database non si mostra mai. */
export const revokeQuoteErrorKey = (error: unknown): TranslationKey => {
  const message = (error as { message?: string })?.message ?? '';
  const known = ['forbidden', 'itinerary_has_payments', 'itinerary_not_sent', 'itinerary_not_found'];
  const match = known.find((key) => message.includes(key));
  return match
    ? (`builder.pricingLock.errors.${match}` as TranslationKey)
    : 'builder.pricingLock.errors.generic';
};

/**
 * Update itinerary status
 */
export const updateItineraryStatus = async (
  itineraryId: string,
  status: string
): Promise<boolean> => {
  try {
    const { error } = await supabase
      .from('itineraries')
      .update({ status })
      .eq('id', itineraryId);

    if (error) {
      console.error('Error updating itinerary status:', error);
      return false;
    }

    return true;
  } catch (error) {
    console.error('Error updating itinerary status:', error);
    return false;
  }
};

// ---------------------------------------------------------------------------
// Anteprima della conversione in template
//
// `save_itinerary_as_template` riscrive le date reali in giorni relativi 1-based
// contati dalla data di inizio della sorgente. Qui si ricalcola la stessa mappatura
// *senza scrivere nulla*, per mostrarla all'utente prima che confermi: le regole
// (base = start_date, indici tagliati a >= 1, fallback sugli indici gia' presenti)
// sono le stesse della RPC, tenerle allineate e' un requisito.
// ---------------------------------------------------------------------------

/** Una attivita' nell'anteprima: solo cio' che serve a riconoscerla in elenco. */
export interface TemplatePreviewActivity {
  id: string;
  title: string;
  /** `type` del database (accommodation, flight, ...): la UI ci mappa l'icona. */
  type: string;
  /** Giorno relativo di inizio, 1-based. */
  startDayIndex: number;
  /** Giorno relativo di fine: uguale a quello di inizio per le attivita' di un giorno. */
  endDayIndex: number;
}

/** Un giorno dell'anteprima: la data che aveva e le attivita' che ci iniziano. */
export interface TemplatePreviewDay {
  /** Posizione 1-based nel template. */
  index: number;
  /** Data originale (ISO), null se la sorgente non ha date. */
  date: string | null;
  activities: TemplatePreviewActivity[];
}

export interface TemplatePreview {
  /** Durata del template che verra' creato: e' il dato principale della modale. */
  durationDays: number;
  startDate: string | null;
  endDate: string | null;
  activitiesCount: number;
  days: TemplatePreviewDay[];
}

/** Riga come la restituisce `get_itinerary_activities` (SETOF itinerary_activities). */
type PreviewActivityRow = Pick<
  Database['public']['Functions']['get_itinerary_activities']['Returns'][number],
  'id' | 'title' | 'type' | 'date' | 'start_date' | 'end_date' | 'start_day_index' | 'end_day_index'
>;

/** Solo la parte data di un timestamp/date del database. */
const isoDay = (value?: string | null): string | null => (value ? value.split('T')[0] : null);

/**
 * Riepilogo di come un itinerario verra' convertito in template.
 *
 * Le attivita' passano da `get_itinerary_activities` e non da una SELECT diretta:
 * e' la strada gia' usata dal resto del frontend ed e' quella che applica i filtri
 * server-side sui campi riservati.
 *
 * Solleva: il chiamante degrada mostrando un messaggio al posto dell'anteprima,
 * l'anteprima non deve mai bloccare il salvataggio.
 */
export const fetchTemplatePreview = async (itineraryId: string): Promise<TemplatePreview> => {
  const [itineraryResult, activitiesResult] = await Promise.all([
    supabase
      .from('itineraries')
      .select('start_date, end_date, duration_days')
      .eq('id', itineraryId)
      .single(),
    supabase.rpc('get_itinerary_activities', { p_itinerary_id: itineraryId }),
  ]);

  if (itineraryResult.error) throw itineraryResult.error;
  if (activitiesResult.error) throw activitiesResult.error;

  const source = itineraryResult.data as {
    start_date: string | null;
    end_date: string | null;
    duration_days: number | null;
  };

  const startDate = isoDay(source.start_date);
  const endDate = isoDay(source.end_date);

  // Stessa formula della RPC: date reali, poi eventuale durata relativa, poi 1.
  const durationDays = Math.max(
    startDate && endDate
      ? inclusiveDayCount(startDate, endDate)
      : source.duration_days ?? 1,
    1,
  );

  const rows: PreviewActivityRow[] = activitiesResult.data ?? [];

  const activities: TemplatePreviewActivity[] = rows.map((row) => {
    const rowStart = isoDay(row.start_date) ?? isoDay(row.date);
    const rowEnd = isoDay(row.end_date) ?? rowStart;

    const startIndex = Math.max(
      startDate && rowStart ? diffDays(startDate, rowStart) + 1 : row.start_day_index ?? 1,
      1,
    );
    const endIndex = Math.max(
      startDate && rowEnd ? diffDays(startDate, rowEnd) + 1 : row.end_day_index ?? startIndex,
      startIndex,
    );

    return {
      id: row.id,
      title: row.title ?? '',
      type: row.type ?? '',
      startDayIndex: startIndex,
      endDayIndex: endIndex,
    };
  });

  // Le attivita' multi-giorno stanno sul giorno in cui iniziano e dichiarano il loro
  // intervallo: ripeterle su ogni giorno coperto renderebbe illeggibile l'elenco.
  // Raggruppamento in una passata sola: con molti giorni un filter per giorno
  // costerebbe O(giorni x attivita').
  const activitiesByDay = new Map<number, TemplatePreviewActivity[]>();
  for (const activity of activities) {
    const bucket = activitiesByDay.get(activity.startDayIndex);
    if (bucket) bucket.push(activity);
    else activitiesByDay.set(activity.startDayIndex, [activity]);
  }

  const days: TemplatePreviewDay[] = Array.from({ length: durationDays }, (_, i) => ({
    index: i + 1,
    date: startDate ? addDays(startDate, i) : null,
    activities: activitiesByDay.get(i + 1) ?? [],
  }));

  return {
    durationDays,
    startDate,
    endDate,
    activitiesCount: activities.length,
    days,
  };
};
